import { useMemo, useEffect } from "react";
import type {
  CreateMaterialCodePayload,
  MaterialSupplierDetail,
} from "@/api/materialSupplierService";
import {
  createMatCode,
  calculateMaterialCodePrice,
} from "@/utils/materialCodeUtils";
import type { FirstTierSupplier } from "@/api/supplierService";

interface UseMaterialCodeLogicParams {
  material: CreateMaterialCodePayload;
  setMaterial: (data: CreateMaterialCodePayload) => void;
  materialSupplierDetail?: MaterialSupplierDetail;
  dimensionName: string;
  secondTierSuppliers?: Array<{ id: number; name: string }>;
  returnScrapSuppliers?: Array<{ id: number; name: string }>;
  firstTierSupplier?: Array<FirstTierSupplier>;
}

export const useMaterialCodeLogic = ({
  material,
  setMaterial,
  materialSupplierDetail,
  dimensionName,
  secondTierSuppliers,
  returnScrapSuppliers,
  firstTierSupplier,
}: UseMaterialCodeLogicParams) => {
  const matSpec = useMemo(() => {
    return materialSupplierDetail?.material_type_ids?.find(
      (type) => type.id === material?.material_type_id
    )?.material_specs;
  }, [materialSupplierDetail, material]);

  const matCode = useMemo(
    () =>
      createMatCode(
        materialSupplierDetail?.name_abbr || "",
        firstTierSupplier?.find(
          (s) => s.id === material?.first_tier_supplier_id
        )?.supplier_abbr || "",
        secondTierSuppliers?.find(
          (s) => s.id === material?.second_tier_supplier_id
        )?.name || "",
        returnScrapSuppliers?.find(
          (s) => s.id === material?.return_scrap_supplier_id
        )?.name || "",
        matSpec?.find((s) => s.material_spec_id === material?.material_spec_id)
          ?.spec_name || "",
        dimensionName,
        material?.dimension_parameters || {}
      ),
    [
      dimensionName,
      firstTierSupplier,
      matSpec,
      materialSupplierDetail?.name_abbr,
      material?.dimension_parameters,
      material?.first_tier_supplier_id,
      material?.material_spec_id,
      material?.return_scrap_supplier_id,
      material?.second_tier_supplier_id,
      returnScrapSuppliers,
      secondTierSuppliers,
    ]
  );

  const matPrice = useMemo(() => {
    return calculateMaterialCodePrice(
      matSpec?.find(
        (spec) => spec.material_spec_id === material?.material_spec_id
      )?.base_price || 0,
      {
        ...materialSupplierDetail?.attributes,
        ...material?.formula_parameters,
      },
      material?.others_cost_detail || [],
      materialSupplierDetail?.formula.formula_js || "",
      {
        rounding: materialSupplierDetail?.attributes.rounding,
        roundingDigits: materialSupplierDetail?.attributes.rounding_digits,
      }
    );
  }, [
    matSpec,
    materialSupplierDetail?.attributes,
    materialSupplierDetail?.formula.formula_js,
    material?.formula_parameters,
    material?.others_cost_detail,
    material?.material_spec_id,
  ]);

  useEffect(() => {
    if (material && material.material_price !== matPrice) {
      setMaterial({
        ...material,
        material_price: matPrice,
      });
    }
  }, [matPrice, material, setMaterial]);

  useEffect(() => {
    if (matCode && material?.material_code !== matCode) {
      setMaterial({
        ...material,
        material_code: matCode,
      });
    }
  }, [matCode, material, setMaterial]);
};
